/**
 * useSwipeRight (A43.1) — pointer-жест «свайп вправо по строке»: строка едет
 * transform'ом за пальцем, из-под неё показывается green-зона действия;
 * отпускание за порогом COMMIT_PX зовёт onCommit, раньше — пружина назад
 * (транзиция у потребителя, active=false её включает).
 *
 * Разграничение жестов (пороги согласованы со SlotGridEditor):
 *  - long-press-drag взводится ТАЙМЕРОМ 350мс без движения (>8px отменяет),
 *    свайп — только ДВИЖЕНИЕМ (ACT_PX по горизонтали) — не пересекаются;
 *  - вертикальный скролл: до взвода |dy| ≥ VETO_Y хоронит жест (намерение
 *    вертикальное); на строке нужен CSS touch-action: pan-y — вертикальную
 *    прокрутку ведёт браузер, горизонталь остаётся pointer-событиям;
 *  - тап/double-tap: без движения жест не взводится, click проходит; после
 *    свайпа click глушится onClickCapture (паттерн draggedRef слот-чипов).
 */
import {
  useRef, useState,
  type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent,
} from "react";

/** Порог срабатывания (~56px — половина узкой строки, мимо не смахнёшь). */
const COMMIT_PX = 56;
/** Взвод жеста: горизонталь > ACT_PX и круче диагонали RATIO. */
const ACT_PX = 12;
const RATIO = 1.4;
/** До взвода: |dy| больше — намерение вертикальное, жест хоронится. */
const VETO_Y = 14;
/** Резина за порогом: дальше COMMIT_PX строка едет на 35% движения. */
const RUBBER = 0.35;
const MAX_PX = 96;

export interface SwipeRightHandlers {
  onPointerDown: (e: ReactPointerEvent<HTMLElement>) => void;
  onPointerMove: (e: ReactPointerEvent<HTMLElement>) => void;
  onPointerUp: (e: ReactPointerEvent<HTMLElement>) => void;
  onPointerCancel: (e: ReactPointerEvent<HTMLElement>) => void;
  onClickCapture: (e: ReactMouseEvent<HTMLElement>) => void;
}

export interface SwipeRight {
  /** Смещение строки, px (0 — покой). */
  dx: number;
  /** Жест взведён (палец ведёт строку) — транзиции выключить. */
  active: boolean;
  /** Порог пройден — отпускание сработает (подсветка зоны). */
  ready: boolean;
  handlers: SwipeRightHandlers;
}

export function useSwipeRight(onCommit: () => void, disabled = false): SwipeRight {
  const [dx, setDx] = useState(0);
  const [active, setActive] = useState(false);
  const [ready, setReady] = useState(false);
  const pid = useRef(-1);
  const x0 = useRef(0), y0 = useRef(0);
  const engaged = useRef(false); // взведён (движется за пальцем)
  const dead = useRef(false); // похоронен вертикальным намерением
  const swiped = useRef(false); // глушить click после жеста
  const commitRef = useRef(onCommit);
  commitRef.current = onCommit;

  const reset = () => {
    pid.current = -1;
    engaged.current = false;
    dead.current = false;
    setDx(0);
    setActive(false);
    setReady(false);
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLElement>) => {
    if (disabled || pid.current !== -1 || !e.isPrimary) return;
    pid.current = e.pointerId;
    x0.current = e.clientX;
    y0.current = e.clientY;
    engaged.current = false;
    dead.current = false;
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLElement>) => {
    if (e.pointerId !== pid.current || dead.current) return;
    /* 31.08: мышь без capture может отпуститься ВНЕ строки до взвода — pid
       оставался занят навсегда, и строка ездила за курсором при наведении. */
    if (!engaged.current && e.pointerType === "mouse" && e.buttons === 0) { pid.current = -1; return; }
    const rawX = e.clientX - x0.current;
    const rawY = e.clientY - y0.current;
    if (!engaged.current) {
      if (Math.abs(rawY) > VETO_Y && Math.abs(rawY) >= rawX) { dead.current = true; return; }
      if (rawX < ACT_PX || rawX < Math.abs(rawY) * RATIO) return;
      engaged.current = true;
      swiped.current = true;
      setActive(true);
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* уже отпущен */ }
    }
    const d = Math.max(0, rawX);
    const off = d <= COMMIT_PX ? d : COMMIT_PX + (d - COMMIT_PX) * RUBBER;
    setDx(Math.min(MAX_PX, off));
    setReady(d >= COMMIT_PX);
  };

  const onPointerUp = (e: ReactPointerEvent<HTMLElement>) => {
    if (e.pointerId !== pid.current) return;
    const fire = engaged.current && e.clientX - x0.current >= COMMIT_PX && e.type !== "pointercancel";
    reset(); // active=false → CSS-транзиция пружинит строку в 0
    window.setTimeout(() => { swiped.current = false; }, 0); // click этого же тапа успевает попасть под глушение
    if (fire) commitRef.current();
  };

  /* После свайпа pointerup рождает click по строке — глушим (иначе карточка
     раскрылась бы вдогонку к открытию формы дубликата). */
  const onClickCapture = (e: ReactMouseEvent<HTMLElement>) => {
    if (!swiped.current) return;
    e.preventDefault();
    e.stopPropagation();
  };

  return {
    dx, active, ready,
    handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp, onClickCapture },
  };
}
